<template>
  <DialogLayout
    v-model:show="showModel"
    body-padding="0"
    show-content-border
    title="选择成员"
    width="800px"
  >
    <div class="person-select">
      <div class="person-select__main">
        <div class="person-select__left">
          <el-tabs
            v-if="showDepartment || showRole"
            v-model="activeTab"
            class="person-select__tabs"
          >
            <el-tab-pane label="选人员" name="user">
              <PersonList
                :is-license="isLicense"
                :selected-list="selectedList"
                :show="showModel"
                @select="toggleUser"
                @select-all="handleUserSelectAll"
              />
            </el-tab-pane>
            <el-tab-pane v-if="showDepartment" label="选部门" lazy name="dept">
              <DepartmentList
                :selected-list="selectedList"
                :show="showModel"
                @check-change="handleDepartmentCheckChange"
              />
            </el-tab-pane>
            <el-tab-pane v-if="showRole" label="选角色" lazy name="role">
              <RoleList :selected-list="selectedList" :show="showModel" @select="toggleRole" />
            </el-tab-pane>
          </el-tabs>

          <PersonList
            v-else
            :is-license="isLicense"
            :selected-list="selectedList"
            :show="showModel"
            @select="toggleUser"
            @select-all="handleUserSelectAll"
          />
        </div>

        <div class="person-select__right">
          <div class="person-select__selected-title">
            已选择（{{
              max !== undefined ? `${selectedList.length}/${max}` : selectedList.length
            }}）
          </div>
          <div class="person-select__selected-list">
            <el-tag
              v-for="item in selectedList"
              :key="`${item.property}-${item.id}`"
              class="person-select__tag"
              closable
              type="info"
              @close="removeItem(item)"
            >
              {{ item.name }}
            </el-tag>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <el-button @click="showModel = false">取消</el-button>
      <el-button type="primary" @click="handleConfirm">确定</el-button>
    </template>
  </DialogLayout>
</template>

<script setup>
import { ref, watch } from 'vue'
import DialogLayout from '@/components/layout/dialog-layout/index.vue'
import DepartmentList from './department-list.vue'
import PersonList from './person-list.vue'
import RoleList from './role-list.vue'

defineOptions({
  name: 'PersonSelect'
})

// 控制选择弹窗的显示与隐藏，对应 v-model:show
const showModel = defineModel('show', { type: Boolean, default: false })
// 已选择的人员、部门或角色列表，对应组件默认 v-model
const selectedModel = defineModel({ type: Array, default: () => [] })
const emits = defineEmits(['confirm'])
const props = defineProps({
  // 0：仅拥有 AI 权限的用户；1：所有用户
  isLicense: {
    type: Number,
    default: 0
  },
  // 最多可选择的人员、部门和角色总数；不传或传入 undefined 表示不限制，0 表示不可选择
  max: {
    type: Number
  },
  // 是否显示“选部门”tab；默认显示，关闭且未开启角色时不显示 tab 栏
  showDepartment: {
    type: Boolean,
    default: true
  },
  // 是否显示“选角色”tab；默认不显示
  showRole: {
    type: Boolean,
    default: false
  }
})

const activeTab = ref('user')
const selectedList = ref([])
// 接口返回的 ID 可能是数字，权限配置 JSON 解析后的 ID 可能是字符串，比较前统一类型
const isSameId = (left, right) => String(left) === String(right)

const syncSelected = () => {
  selectedList.value = selectedModel.value.map((item) => ({ property: 'user', ...item }))
}

const toggleUser = (item) => {
  if (!item.userId) return

  if (
    selectedList.value.some(
      (selected) => selected.property === 'user' && isSameId(selected.id, item.userId)
    )
  ) {
    selectedList.value = selectedList.value.filter(
      (selected) => selected.property !== 'user' || !isSameId(selected.id, item.userId)
    )
    return
  }

  if (props.max !== undefined && selectedList.value.length >= props.max) return
  selectedList.value.push({
    ...item,
    id: item.userId,
    name: item.name,
    property: 'user'
  })
}

const handleUserSelectAll = (users, checked) => {
  const userIds = new Set(users.map((item) => String(item.userId)))

  if (!checked) {
    selectedList.value = selectedList.value.filter(
      (item) => item.property !== 'user' || !userIds.has(String(item.id))
    )
    return
  }

  const selectedUserIds = new Set(
    selectedList.value.filter((item) => item.property === 'user').map((item) => String(item.id))
  )
  const availableCount =
    props.max !== undefined ? Math.max(0, props.max - selectedList.value.length) : users.length
  const usersToAdd = users
    .filter((item) => !selectedUserIds.has(String(item.userId)))
    .slice(0, availableCount)
    .map((item) => ({
      ...item,
      id: item.userId,
      name: item.name,
      property: 'user'
    }))

  selectedList.value.push(...usersToAdd)
}

const handleDepartmentCheckChange = (item, checked) => {
  if (!item.pid) return

  if (!checked) {
    selectedList.value = selectedList.value.filter(
      (selected) => selected.property !== 'dept' || !isSameId(selected.id, item.pid)
    )
    return
  }

  if (
    selectedList.value.some(
      (selected) => selected.property === 'dept' && isSameId(selected.id, item.pid)
    ) ||
    (props.max !== undefined && selectedList.value.length >= props.max)
  )
    return
  selectedList.value.push({
    ...item,
    id: item.pid,
    name: item.text,
    property: 'dept'
  })
}

const toggleRole = (item) => {
  if (!item.id) return

  if (
    selectedList.value.some(
      (selected) => selected.property === 'role' && isSameId(selected.id, item.id)
    )
  ) {
    selectedList.value = selectedList.value.filter(
      (selected) => selected.property !== 'role' || !isSameId(selected.id, item.id)
    )
    return
  }

  if (props.max !== undefined && selectedList.value.length >= props.max) return
  selectedList.value.push({
    ...item,
    name: item.roleName,
    property: 'role'
  })
}

const removeItem = (item) => {
  selectedList.value = selectedList.value.filter(
    (selected) => selected.property !== item.property || !isSameId(selected.id, item.id)
  )
}

const handleConfirm = () => {
  selectedModel.value = [...selectedList.value]
  emits('confirm', selectedModel.value)
  showModel.value = false
}

watch(
  () => showModel.value,
  (visible) => {
    if (!visible) return
    syncSelected()
    activeTab.value = 'user'
  }
)

watch(
  () => selectedModel.value,
  () => {
    if (showModel.value) {
      syncSelected()
    }
  },
  { deep: true }
)
</script>

<style scoped lang="less">
.person-select {
  height: 454px;

  &__main {
    display: flex;
    height: 100%;
    overflow: hidden;
  }

  &__left,
  &__right {
    box-sizing: border-box;
    width: 400px;
  }

  &__left {
    display: flex;
    flex-direction: column;
    border-right: 1px solid #f2f3f5;
  }

  &__tabs {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;

    :deep(.el-tabs__header) {
      flex: 0 0 auto;
      margin: 0;
    }

    :deep(.el-tabs__nav-wrap) {
      padding: 0 16px;
    }

    :deep(.el-tabs__content) {
      flex: 1;
      min-height: 0;

      overflow-y: auto;
    }

    :deep(.el-tab-pane) {
      min-height: 100%;
    }

    :deep(.person-select__person-list),
    :deep(.person-select__department-list),
    :deep(.person-select__role-list) {
      display: block;
      min-height: auto;
    }

    :deep(.person-select__list),
    :deep(.person-select__tree) {
      overflow: visible;
    }

    :deep(.el-tabs__nav-wrap::after) {
      height: 1px;
      background-color: #f2f3f5;
    }

    :deep(.el-tabs__active-bar) {
      background-color: #7a52f4;
    }

    :deep(.el-tabs__item) {
      color: #646566;
      font-weight: 400;
    }

    :deep(.el-tabs__item.is-active) {
      color: #323233;
      font-weight: 600;
    }
  }

  &__right {
    padding: 16px;
    overflow-y: scroll;
  }

  &__selected-title {
    margin-bottom: 12px;
    color: #969799;
    font-size: 14px;
    line-height: 20px;
  }

  &__selected-list {
    max-height: 390px;
    overflow-y: auto;
  }

  &__tag {
    height: 28px;
    margin-right: 8px;
    margin-bottom: 8px;
    border: none;
    border-radius: 8px;
    background: #f7f8fa;
    color: #323233;
  }
}
</style>
